import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { CHART_COLORS, DONUT_COLORS, DONUT_OTHER_COLOR } from '../typings/chart';
import { ChartPointModel }                               from '../typings/intelligence.chart-point';
import { ChartSeriesModel }                              from '../typings/intelligence.chart-series';
import { DonutSegmentModel }                             from '../typings/intelligence.donut-segment';
import { IntelligenceCustomersModel }                    from '../typings/intelligence.customers';
import { IntelligenceFormatService }                     from '../intelligence.format.service';
import { IntelligenceService }                           from '../intelligence.service';
import { IntelligenceTopCustomerModel }                  from '../typings/intelligence.top-customer';
import { TableSortModel }                                from '../typings/intelligence.table-sort';

@Component({
  selector   : 'wor-intelligence-customers-tab',
  template: require('./intelligence.customers-tab.component.html')
})
export class CustomersTabComponent implements OnInit, OnDestroy {
  error = false;
  loading = true;

  invoiceSourceSegments : Array<DonutSegmentModel> = [];
  jobMixSegments : Array<DonutSegmentModel> = [];
  newVsReturningPoints : Array<ChartPointModel> = [];
  sort : TableSortModel = { direction: 'desc', field: 'revenue_ex_tax' };
  sourceSegments : Array<DonutSegmentModel> = [];
  topCustomers : Array<IntelligenceTopCustomerModel> = [];
  topCustomerTotals = { gross_profit_ex_tax: 0, invoice_count: 0, revenue_ex_tax: 0 };

  newVsReturningSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'new_customers', label: this.translateService.instant('INTELLIGENCE_SPACE.KPIS.NEW_CUSTOMERS') },
    { color: CHART_COLORS[ 2 ], key: 'returning_customers_invoiced', label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.RETURNING') }
  ];

  private loadGeneration = 0;
  private subscription : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  averageInvoice ( customer : IntelligenceTopCustomerModel ) : number {
    return customer.invoice_count
      ? customer.revenue_ex_tax / customer.invoice_count
      : 0;
  }

  formatMoney ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  isInvoiceSourcesEmpty () : boolean {
    return !this.invoiceSourceSegments.some(segment => segment.value > 0);
  }

  isJobMixEmpty () : boolean {
    return !this.jobMixSegments.some(segment => segment.value > 0);
  }

  isNewVsReturningEmpty () : boolean {
    return !this.newVsReturningPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  isSourcesEmpty () : boolean {
    return !this.sourceSegments.some(segment => segment.value > 0);
  }

  isTopCustomersEmpty () : boolean {
    return !this.topCustomers.length;
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getCustomers()
    .then(( data : IntelligenceCustomersModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.newVsReturningPoints = ( data.series || [] ).map(day => ({
        label : this.intelligenceFormatService.date(day.date),
        values: {
          new_customers               : day.new_customers,
          returning_customers_invoiced: day.returning_customers_invoiced
        }
      }));

      this.sourceSegments = this.buildSourceSegments(data.sources || {});
      this.invoiceSourceSegments = this.buildSourceSegments(data.invoice_sources || {});

      const mix = data.job_mix || { other: 0, repairs: 0, services: 0 };

      this.jobMixSegments = [
        { color: CHART_COLORS[ 1 ], label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.SERVICES'), value: mix.services },
        { color: CHART_COLORS[ 2 ], label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.REPAIRS'), value: mix.repairs },
        { color: CHART_COLORS[ 0 ], label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'), value: mix.other }
      ];

      this.topCustomers = this.intelligenceFormatService.sortRows(data.top_customers || [], this.sort.field, this.sort.direction);
      this.topCustomerTotals = this.topCustomers.reduce(( totals, customer ) => ({
        gross_profit_ex_tax: totals.gross_profit_ex_tax + customer.gross_profit_ex_tax,
        invoice_count      : totals.invoice_count + customer.invoice_count,
        revenue_ex_tax     : totals.revenue_ex_tax + customer.revenue_ex_tax
      }), { gross_profit_ex_tax: 0, invoice_count: 0, revenue_ex_tax: 0 });
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  topCustomersCsvRows () : Array<any> {
    return this.topCustomers.map(customer => ({
      customer           : customer.display_name,
      invoice_count      : customer.invoice_count,
      revenue_ex_tax     : customer.revenue_ex_tax,
      avg_per_invoice    : Math.round(this.averageInvoice(customer) * 100) / 100,
      gross_profit_ex_tax: customer.gross_profit_ex_tax
    }));
  }

  sortBy ( field : string ) : void {
    this.sort = {
      direction: this.sort.field === field && this.sort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.topCustomers = this.intelligenceFormatService.sortRows(this.topCustomers, this.sort.field, this.sort.direction);
  }

  private buildSourceSegments ( counts : { [ label : string ] : number } ) : Array<DonutSegmentModel> {
    const entries = Object.keys(counts)
      .map(label => ({ count: counts[ label ] || 0, label }))
      .sort(( first, second ) => second.count - first.count);

    const kept = entries.slice(0, DONUT_COLORS.length);
    const remainder = entries.slice(DONUT_COLORS.length).reduce(( sum, entry ) => sum + entry.count, 0);

    const segments = kept.map(( entry, index ) => ({
      color: DONUT_COLORS[ index ],
      label: entry.label,
      value: entry.count
    }));

    if ( remainder > 0 ) {
      segments.push({
        color: DONUT_OTHER_COLOR,
        label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
        value: remainder
      });
    }

    return segments;
  }
}
